/**
 * StorageUsage Component
 *
 * Displays storage quota usage with visual bar and breakdown.
 */

import * as React from 'react';
import { AlertCircle, HardDrive, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useStorage } from '@/lib/hooks/useStorage';

export interface StorageUsageProps {
  /** Callback when upgrade is clicked */
  onUpgrade?: () => void;
  /** Show detailed breakdown */
  showBreakdown?: boolean;
  /** Show recent operations */
  showOperations?: boolean;
  /** Compact mode (just the bar) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Storage usage display component
 */
export function StorageUsage({
  onUpgrade,
  showBreakdown = true,
  showOperations = false,
  compact = false,
  className,
}: StorageUsageProps) {
  const {
    usage,
    operations,
    usedPercentage,
    isApproachingLimit,
    isAtLimit,
    totalFormatted,
    quotaFormatted,
    fetchOperations,
  } = useStorage({
    autoFetch: true,
  });

  React.useEffect(() => {
    if (showOperations) {
      fetchOperations({ limit: 10 });
    }
  }, [showOperations, fetchOperations]);

  if (compact) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Storage</span>
          <span>
            {totalFormatted} / {quotaFormatted}
          </span>
        </div>
        <UsageBar percentage={usedPercentage} isAtLimit={isAtLimit} isApproachingLimit={isApproachingLimit} />
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <HardDrive className="h-4 w-4" />
          Storage Usage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Usage bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold">{totalFormatted}</span>
            <span className="text-sm text-muted-foreground">of {quotaFormatted}</span>
          </div>
          <UsageBar percentage={usedPercentage} isAtLimit={isAtLimit} isApproachingLimit={isApproachingLimit} />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{usedPercentage.toFixed(1)}% used</span>
            {usage && (
              <span className="text-muted-foreground">{usage.fileCount} files</span>
            )}
          </div>
        </div>

        {/* Warning messages */}
        {isAtLimit && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Storage limit reached</p>
              <p className="text-destructive/80">
                You've used all your storage. Upgrade your plan or delete files to continue uploading.
              </p>
            </div>
          </div>
        )}

        {isApproachingLimit && !isAtLimit && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 text-sm">
            <TrendingUp className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Approaching storage limit</p>
              <p className="opacity-80">
                You've used {usedPercentage.toFixed(0)}% of your storage. Consider upgrading your plan.
              </p>
            </div>
          </div>
        )}

        {/* Breakdown by category */}
        {showBreakdown && usage?.breakdown && usage.breakdown.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Breakdown by category</h4>
            <div className="space-y-2">
              {usage.breakdown.map((item) => (
                <div key={item.category} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: getCategoryColor(item.category) }}
                    />
                    <span className="capitalize">{item.category}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">{item.fileCount} files</span>
                    <span className="w-20 text-right">{item.formatted}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent operations */}
        {showOperations && operations.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Recent activity</h4>
            <div className="space-y-2">
              {operations.slice(0, 5).map((op) => (
                <div key={op.operationId} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'capitalize',
                      op.operationType === 'delete' && 'text-destructive'
                    )}>
                      {op.operationType}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>{op.bytesFormatted}</span>
                    <span className="w-20 text-right">
                      {new Date(op.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upgrade button */}
        {(isApproachingLimit || isAtLimit) && onUpgrade && (
          <Button onClick={onUpgrade} className="w-full">
            Upgrade Plan
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

interface UsageBarProps {
  percentage: number;
  isAtLimit: boolean;
  isApproachingLimit: boolean;
}

function UsageBar({ percentage, isAtLimit, isApproachingLimit }: UsageBarProps) {
  return (
    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
      <div
        className={cn(
          'h-full transition-all duration-500',
          isAtLimit
            ? 'bg-destructive'
            : isApproachingLimit
              ? 'bg-yellow-500'
              : 'bg-primary'
        )}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    general: '#6366f1',
    document: '#8b5cf6',
    financial: '#f59e0b',
    media: '#ec4899',
    generated: '#10b981',
  };
  return colors[category] || '#6b7280';
}

export default StorageUsage;
